<template>
  <div class="w-full flex flex-col items-start gap-6 animate-in fade-in">
    <!-- Standard Notification List -->
    <div v-if="unapprovedCount > 0 || returnCount > 0 || prCount > 0" class="w-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      
      <!-- List Item: Store Requests -->
      <div 
        v-if="unapprovedCount > 0"
        @click="goToApproval"
        class="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-all border-b border-slate-100 last:border-0 group text-left"
      >
        <div class="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center text-amber-600 flex-shrink-0 group-hover:scale-110 transition-transform">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        
        <div class="flex-1 min-w-0">
          <h4 class="text-[0.82rem] font-black text-[#0d4169] truncate">Store Approval Required</h4>
          <p class="text-[0.72rem] text-slate-500 font-medium">
            You have <span class="text-amber-600 font-black">{{ unapprovedCount }}</span> store requests waiting for your review.
          </p>
        </div>

        <div class="flex items-center gap-1 text-[#0d4169]/40 group-hover:text-[#0d4169] transition-colors">
          <span class="text-[9px] font-black tracking-widest hidden sm:inline">View Details</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        </div>
      </div>

      <!-- List Item: Returns -->
      <div 
        v-if="returnCount > 0"
        @click="goToReturn"
        class="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-all border-b border-slate-100 last:border-0 group text-left"
      >
        <div class="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600 flex-shrink-0 group-hover:scale-110 transition-transform">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 8.959 8.959 0 01-9 9m9-9h.01" />
          </svg>
        </div>
        
        <div class="flex-1 min-w-0">
          <h4 class="text-[0.82rem] font-black text-[#0d4169] truncate">Return Approval Required</h4>
          <p class="text-[0.72rem] text-slate-500 font-medium">
            You have <span class="text-emerald-600 font-black">{{ returnCount }}</span> returns waiting for your review.
          </p>
        </div>

        <div class="flex items-center gap-1 text-[#0d4169]/40 group-hover:text-[#0d4169] transition-colors">
          <span class="text-[9px] font-black tracking-widest hidden sm:inline">View Details</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        </div>
      </div>

      <!-- List Item: Purchase Requisitions -->
      <div 
        v-if="prCount > 0"
        @click="goToPR"
        class="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-all border-b border-slate-100 last:border-0 group text-left"
      >
        <div class="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600 flex-shrink-0 group-hover:scale-110 transition-transform">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        </div>
        
        <div class="flex-1 min-w-0">
          <h4 class="text-[0.82rem] font-black text-[#0d4169] truncate">Purchase Requisition Approval Required</h4>
          <p class="text-[0.72rem] text-slate-500 font-medium">
            You have <span class="text-indigo-600 font-black">{{ prCount }}</span> purchase requisitions waiting for your review.
          </p>
        </div>

        <div class="flex items-center gap-1 text-[#0d4169]/40 group-hover:text-[#0d4169] transition-colors">
          <span class="text-[9px] font-black tracking-widest hidden sm:inline">View Details</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div 
      v-if="!loading && unapprovedCount === 0 && returnCount === 0 && prCount === 0" 
      class="bg-white p-16 rounded-3xl border-2 border-dashed border-slate-200 flex flex-col items-start gap-4 text-slate-400"
    >
      <div class="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      </div>
      <div class="text-left w-full">
        <p class="text-lg font-bold text-slate-600">All caught up!</p>
        <p class="text-sm text-slate-400">No new notifications at the moment.</p>
      </div>
    </div>
    
    <div v-show="loading" class="flex justify-center p-12">
      <div class="w-8 h-8 border-4 border-[#0d4169]/10 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import store from '../store'
import { apiFetch } from '../utils/api'

const router = useRouter()
const unapprovedCount = ref(0)
const returnCount = ref(0)
const prCount = ref(0)
const loading = ref(true)

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)

async function fetchCounts() {
  if (!store.getters.isAuthenticated || !store.state.token) {
    loading.value = false
    return
  }
  try {
    loading.value = true
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`

    const [srRes, retRes, prRes] = await Promise.all([
      apiFetch('/store-request/pending'),
      apiFetch(`/return?${query}&isApproved=0`),
      apiFetch(`/purchase-requisition?${query}&isApproved=0`)
    ])

    // Store Requests
    if (srRes.ok) {
      const data = await srRes.json()
      const list = data.data || []
      const depts = new Set()
      list.forEach(item => { if (item.department) depts.add(item.department) })
      unapprovedCount.value = depts.size
    }

    // Returns
    if (retRes.ok) {
      const list = await retRes.json()
      const depts = new Set()
      list.forEach(item => { if (item.department?.name) depts.add(item.department.name) })
      returnCount.value = depts.size
    }

    // PRs
    if (prRes.ok) {
      const list = await prRes.json()
      const depts = new Set()
      list.forEach(item => { if (item.department?.name) depts.add(item.department.name) })
      prCount.value = depts.size
    }

  } catch (err) {
    console.error('Error fetching counts:', err)
  } finally {
    loading.value = false
  }
}

function goToApproval() {
  router.push('/inventory/approval?tab=store')
}

function goToReturn() {
  router.push('/inventory/approval?tab=returns')
}

function goToPR() {
  router.push('/inventory/approval?tab=prs')
}

onMounted(() => {
  fetchCounts()
})
</script>

<style scoped>
.animate-in {
  animation: 0.3s ease-out forwards;
}
.fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
